import {ReloadOutlined, SearchOutlined} from '@ant-design/icons/lib';
import {Button} from 'antd';
import {SearchBar} from 'components';
import {FilterInput} from 'components/FilterInput';
import {Formik} from 'formik';
import {DatePicker, Form, Select} from 'formik-antd';
import moment, {Moment} from 'moment';
import React from 'react';
import styled from 'styled-components';
import {useAccessLevel} from '../../hooks/useAccessLevel';

type FilterValues = {
  filterName: string;
  filterValue: string;
  createdAt: [Moment, Moment];
};

export type FilterQueryParams = {
  createdAt?: string[];
  [key: string]: any;
};

type Props = {
  onChange: (values: FilterQueryParams) => void;
  queryFilters: FilterQueryParams;
};

const RangePicker = styled(DatePicker.RangePicker)`
  min-width: 240px;
`;

export const EventFilters: React.FC<Props> = ({onChange, queryFilters}) => {
  const {isAdmin} = useAccessLevel();

  const {createdAt = [], ...filters} = queryFilters;
  const filterName = Object.keys(filters).find((key) => filters[key]) || 'objectId';
  const initialValues: FilterValues = {
    filterName: filterName,
    filterValue: filters[filterName] || '',
    createdAt: [moment(createdAt[0]).startOf('day'), moment(createdAt[1]).endOf('day')]
  };

  const dropdownFilters: {[key: string]: string} = {
    id: 'Event ID',
    objectId: 'Payment ID',
    shopName: 'Store name',
    acquirer: 'Acquirer',
    processor: 'Payment processor',
    customerEmail: 'Customer email',
    customerName: 'Customer name',
    accountId: 'Account ID',
    authorizationCode: 'Authorization code',
    orderId: 'Order ID',
    providerReferenceId: 'Provider reference ID',
    paymentMethodLast4: 'Card last 4 digits',
    paymentMethodMethod: 'Payment method',
    sessionIp: 'Session IP',
    masterAccountId: 'Master acccount',
    billingPlan: 'Billing plan'
  };

  const adminOnlyFilters = ['acquirer', 'processor', 'masterAccountId', 'billingPlan'];

  const handleSubmit = ({createdAt, filterName, filterValue}: FilterValues) => {
    const [startMoment, endMoment] = createdAt;
    const start = startMoment ? startMoment : moment().subtract(1, 'day');
    const end = endMoment ? endMoment : moment();
    const filter: FilterQueryParams = {...filters};
    Object.keys(filter).forEach((key) => {
      (filter as any)[key] = key === filterName ? filterValue : undefined;
    });
    filter.createdAt = [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
    return onChange(filter);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({dirty, submitForm, values}) => {
        return (
          <Form>
            <SearchBar compact>
              <Select
                name="filterName"
                style={{minWidth: 250}}
                showSearch
                optionFilterProp="children">
                {Object.entries(dropdownFilters).map(([value, label]) => {
                  if (adminOnlyFilters.includes(value) && !isAdmin) return null;
                  return (
                    <Select.Option key={value} value={value}>
                      {label}
                    </Select.Option>
                  );
                })}
              </Select>
              <FilterInput filterName={values.filterName} />
              <RangePicker
                name="createdAt"
                format="L"
                allowClear={false}
                onOpenChange={(isOpen) => !isOpen && submitForm()}
                disabledDate={(date) => (date ? date.isAfter(moment().endOf('day')) : false)}
                ranges={{
                  Today: [moment().startOf('day'), moment().endOf('day')],
                  'Last week': [moment().startOf('day').subtract(7, 'days'), moment().endOf('day')],
                  'Last 14 days': [
                    moment().startOf('day').subtract(14, 'days'),
                    moment().endOf('day')
                  ],
                  'Last month': [
                    moment().startOf('day').subtract(1, 'month'),
                    moment().endOf('day')
                  ],
                  'Last 3 months': [
                    moment().startOf('day').subtract(3, 'month'),
                    moment().endOf('day')
                  ]
                }}
              />
              <Button
                htmlType="submit"
                style={{minWidth: 130}}
                type={dirty ? 'primary' : undefined}>
                {dirty ? (
                  <>
                    <SearchOutlined /> Search
                  </>
                ) : (
                  <>
                    <ReloadOutlined /> Refresh
                  </>
                )}
              </Button>
            </SearchBar>
          </Form>
        );
      }}
    </Formik>
  );
};
